import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@osmosis/react-ui/components/Box';
import { H2, H4, P } from '@osmosis/react-ui/components/Text';
import QuizConfidenceSummary from './QuizConfidenceSummary';
import { IconButton } from '@osmosis/react-ui/components/IconButton';
import Icon from '@osmosis/react-ui/components/Icon';
import {
  faChevronUp,
  faChevronDown,
  faSmile,
} from '@fortawesome/pro-light-svg-icons';
import { textGray600 } from '@osmosis/tailwind-ui/styles';
import { useQuizContext } from '../../context/QuizContext';
import SemiCircleProgressBar from '../SemiCircleProgressBar';

/**
 * Pie Chart which shows the progress of the quiz based on {answerMap} questions answered and filter selected.
 */
const QuizProgress = () => {
  const { correctItems, incorrectItems, totalItems, getGroupQuizResponses } =
    useQuizContext();
  const [isOpen, setIsOpen] = useState(false);
  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const correctCount = correctItems().length;
  const incorrectCount = incorrectItems().length;
  const correctPercent =
    totalItems > 0 ? Math.round((correctCount / totalItems) * 100) : 0;

  const incorrectPercent =
    totalItems > 0
      ? incorrectCount > 0
        ? Math.round((incorrectCount / totalItems) * 100)
        : 0
      : 0;

  const { correctLow, correctMedium, correctHigh } = getGroupQuizResponses(
    correctItems(),
  );
  const { incorrectLow, incorrectMedium, incorrectHigh } =
    getGroupQuizResponses(incorrectItems());

  return (
    <Flex
      width="100%"
      data-testid="quiz-progress"
      flexDirection="column"
      overflowY="scroll"
      sx={{
        border: '2px solid ',
        boxShadow: 'large',
        borderRadius: '4px',
        borderColor: 'gray10',
        m: [0, 3, 0],
        boxSizing: 'border-box',
      }}
    >
      <Flex
        width="100%"
        p={20}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'gray10',
          position: 'relative',
        }}
      >
        <H4 color="black">
          <FormattedMessage
            id="quiz.progress.header"
            defaultMessage="Your progress"
          />
        </H4>
      </Flex>
      <P padding={20}>
        <FormattedMessage
          id="quiz.progress.completed-question"
          defaultMessage="{completed}/{total} questions completed"
          values={{
            completed: incorrectCount + correctCount,
            total: totalItems,
          }}
        />
      </P>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <SemiCircleProgressBar
            correct={correctCount}
            incorrect={incorrectCount}
            total={totalItems}
          />
          <Box
            style={{
              position: 'absolute',
              left: '50%',
              top: '40%',
              transform: 'translate(-50%, -50%)',
              width: 'max-content',
              textAlign: 'center',
              color: '#404040',
            }}
            data-testid="quiz-progress-score-value"
          >
            <H2 color="#404040" data-testid="score-percent">
              <FormattedMessage
                defaultMessage="{correctPercent}%"
                id="quiz.progress.scorePercent"
                values={{ correctPercent }}
              />
            </H2>
            <H2 fontSize={16} data-testid="score-correct-section">
              <FormattedMessage
                defaultMessage="correct"
                id="quiz.progress.scoreDetails"
              />
            </H2>
          </Box>
          <Box>
            <Box
              alignItems="center"
              sx={{ display: 'flex', gap: 2, marginTop: '24px' }}
            >
              <Box className="inline-block w-4 h-4 rounded" bg="#16A34A" />
              <P fontSize={14} data-testid="quiz-correct-count">
                <FormattedMessage
                  defaultMessage="{correctPercent}% correct ({correctCount} questions)"
                  id="quiz.progress.correct.count"
                  values={{ correctPercent, correctCount }}
                />
              </P>
            </Box>
            <Box
              alignItems="center"
              sx={{ display: 'flex', gap: 2, marginTop: '24px' }}
            >
              <Box className="inline-block w-4 h-4 rounded" bg="#B30000" />
              <P fontSize={14} data-testid="quiz-incorrect-count">
                <FormattedMessage
                  defaultMessage="{percent}% incorrect ({count} questions)"
                  id="quiz.progress.incorrect.count"
                  values={{
                    percent: incorrectPercent,
                    count: incorrectCount,
                  }}
                />
              </P>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        className="border rounded-lg p-3"
        sx={{ margin: '20px', padding: '12px 20px' }}
      >
        <Flex
          data-testid="confidence-accuracy-header"
          className="justify-between items-center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Icon
              icon={faSmile}
              size="sm"
              style={{ height: '24px', width: '24px' }}
              data-testid="quiz-progress-smile-icon"
            />
            <P className="ml-2 font-medium text-gray-800">
              <FormattedMessage
                id="quiz.progress.confidence-accuracy.title"
                defaultMessage="Confidence and accuracy"
              />
            </P>
          </Box>

          <IconButton
            data-testid="quiz.progress.toggle-accordion-button"
            onClick={handleToggleOpen}
            variant="transparent"
            icon={<Icon icon={isOpen ? faChevronUp : faChevronDown} />}
            className={`text-lg ${textGray600}`}
          />
        </Flex>

        {isOpen && (
          <Box
            data-testid="confidence-accuracy-details"
            className="text-sm text-gray-600 leading-relaxed"
          >
            <P sx={{ marginY: '15px' }}>
              <FormattedMessage
                id="quiz.progress.confidence-accuracy-content"
                defaultMessage="Here’s your confidence breakdown: below, you'll find the number of questions you got right and wrong at each confidence level-low, medium, and high."
              />
            </P>

            <QuizConfidenceSummary
              color="#16A34A"
              confidence={{
                low: correctLow,
                medium: correctMedium,
                high: correctHigh,
              }}
              sectionType="correct"
            />
            <QuizConfidenceSummary
              color="#B30000"
              confidence={{
                low: incorrectLow,
                medium: incorrectMedium,
                high: incorrectHigh,
              }}
              sectionType="incorrect"
            />
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default QuizProgress;
